import React from "react";
import { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, Legend, PieChart, Pie, Cell } from "recharts";
import { format, parse } from "date-fns";

// Color Palette with intense neon glow properties
const SITE_PIE_COLORS = ["#0054A6", "#1D4ED8", "#3B82F6", "#60A5FA", "#93C5FD", "#3A86FF"];
const PERSONAL_COLORS = { income: "#10B981", expense: "#EF4444" };
const SITE_LINE_COLOR = "#6366F1";
const SUPERVISOR_COLOR = "#F59E0B";
const LABOR_COLOR = "#06B6D4"; // Hyper Cyan

export default function DashboardChartsGrid({ 
  siteWiseSpend = [], 
  personalFlowData = [], 
  supervisorFlowData = [], 
  laborTrendData = []
}) {

  const totalSiteSpend = siteWiseSpend.reduce((acc, curr) => acc + curr.spend, 0);

  const formatXAxisTick = (tickVal) => {
    if (!tickVal) return "";
    if (String(tickVal).includes(":")) {
      try {
        const parsedTime = parse(tickVal, "HH:mm", new Date());
        return format(parsedTime, "hh:mm a");
      } catch {
        return tickVal;
      }
    }
    try {
      return format(new Date(tickVal), "dd MMM");
    } catch {
      return tickVal;
    }
  };

  const commonTooltipProps = {
    contentStyle: { 
      backgroundColor: "rgba(10, 15, 30, 0.95)",
      borderRadius: "20px", 
      border: "1px solid rgba(255, 255, 255, 0.15)", 
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255,255,255,0.2)",
      color: "#fff",
      backdropFilter: "blur(12px)"
    },
    labelFormatter: (label) => {
      if (String(label).includes(":")) {
        try {
          const parsedTime = parse(label, "HH:mm", new Date());
          return `Logged at ${format(parsedTime, "hh:mm a")}`;
        } catch {
          return label;
        }
      }
      try {
        return format(new Date(label), "PPPP");
      } catch {
        return label;
      }
    }
  };

  // Custom 3D Glowing Node Particle
  const GlowingDot = (props) => {
    const { cx, cy, stroke } = props;
    return (
      <g>
        <circle cx={cx} cy={cy} r={9} fill={stroke} opacity={0.25} style={{ filter: `blur(2px)` }} />
        <circle cx={cx} cy={cy} r={5} fill={stroke} style={{ filter: `drop-shadow(0px 0px 8px ${stroke})` }} />
        <circle cx={cx} cy={cy} r={2} fill="#fff" />
      </g>
    );
  };

  return (
    <div className="space-y-8 mt-6">
      
      {/* 🔷 ROW 1: RATIOS & PERSONAL TIMELINE */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* CHART A: SITE RATIO PIE CHART */}
        <ChartCard title="Site Expenditure Analysis" subtitle="Net spend distribution ratio">
          {siteWiseSpend.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center h-full gap-6">
              <div className="w-full sm:w-1/2 h-[240px] relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={siteWiseSpend}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={88}
                      paddingAngle={4}
                      dataKey="spend"
                      stroke="none"
                    >
                      {siteWiseSpend.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={SITE_PIE_COLORS[index % SITE_PIE_COLORS.length]} 
                          className="hover:opacity-90 transition-all duration-300 cursor-pointer" 
                          style={{ filter: "drop-shadow(0px 8px 16px rgba(0,0,0,0.12))" }}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Spend</span>
                  <span className="text-sm font-black text-slate-950">₹{totalSiteSpend.toLocaleString()}</span>
                </div>
              </div>

              <div className="w-full sm:w-1/2 overflow-y-auto max-h-[220px] pr-2 space-y-2 custom-scrollbar">
                {siteWiseSpend.map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 rounded-2xl bg-slate-50 border border-slate-200/60 shadow-xs hover:border-slate-300 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0 shadow-xs" style={{ backgroundColor: SITE_PIE_COLORS[i % SITE_PIE_COLORS.length] }} />
                      <p className="text-[11px] font-black text-slate-700 uppercase truncate max-w-[130px]">{item.name}</p>
                    </div>
                    <p className="text-[11px] font-black text-slate-900 ml-2 bg-white px-2 py-0.5 rounded-md border border-slate-100">
                      {((item.spend / (totalSiteSpend || 1)) * 100).toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState text="No Active Site Spending Transacted" />}
        </ChartCard>

        {/* CHART B: PERSONAL CASH FLOW (3D NEON IMMERSION) */}
        <ChartCard title="Personal Cash Flow" subtitle="Daily Income vs Expenses Timeline" isDark={true}>
          {personalFlowData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={personalFlowData} margin={{ top: 15, right: 15, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="3dInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PERSONAL_COLORS.income} stopOpacity={0.35}/>
                    <stop offset="95%" stopColor={PERSONAL_COLORS.income} stopOpacity={0.01}/>
                  </linearGradient>
                  <linearGradient id="3dExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PERSONAL_COLORS.expense} stopOpacity={0.35}/>
                    <stop offset="95%" stopColor={PERSONAL_COLORS.expense} stopOpacity={0.01}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: "#4B5563" }} tickFormatter={formatXAxisTick} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#4B5563" }} />
                <Tooltip {...commonTooltipProps} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase", paddingTop: "12px", color: "#9CA3AF" }} />
                <Area type="monotone" name="Income" dataKey="income" stroke={PERSONAL_COLORS.income} strokeWidth={3.5} fillOpacity={1} fill="url(#3dInc)" dot={<GlowingDot stroke={PERSONAL_COLORS.income} />} style={{ filter: `drop-shadow(0px 8px 12px ${PERSONAL_COLORS.income}40)` }} />
                <Area type="monotone" name="Expense" dataKey="expense" stroke={PERSONAL_COLORS.expense} strokeWidth={3.5} fillOpacity={1} fill="url(#3dExp)" dot={<GlowingDot stroke={PERSONAL_COLORS.expense} />} style={{ filter: `drop-shadow(0px 8px 12px ${PERSONAL_COLORS.expense}40)` }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No Transactions Managed Today" isDark={true} />}
        </ChartCard>
      </div>

      {/* 🔷 ROW 2: 3D GRID TIMELINES BOX */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* CHART C: SITE COST PROGRESSION */}
        <ChartCard title="Site Cost Progression" subtitle="Chronological project spending scale" isDark={true}>
          {siteWiseSpend.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={siteWiseSpend} margin={{ top: 15, right: 15, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="3dSite" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SITE_LINE_COLOR} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={SITE_LINE_COLOR} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: "#4B5563" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#4B5563" }} />
                <Tooltip contentStyle={commonTooltipProps.contentStyle} />
                <Area type="monotone" name="Spend" dataKey="spend" stroke={SITE_LINE_COLOR} strokeWidth={3.5} fillOpacity={1} fill="url(#3dSite)" dot={<GlowingDot stroke={SITE_LINE_COLOR} />} style={{ filter: `drop-shadow(0px 8px 16px ${SITE_LINE_COLOR}60)` }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No Spending Mapped" isDark={true} />}
        </ChartCard>

        {/* CHART D: SUPERVISOR OUTLAY TRENDS */}
        <ChartCard title="Supervisor Field Costs" subtitle="Operational field outlays timeline" isDark={true}>
          {supervisorFlowData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={supervisorFlowData} margin={{ top: 15, right: 15, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="3dSup" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SUPERVISOR_COLOR} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={SUPERVISOR_COLOR} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: "#4B5563" }} tickFormatter={formatXAxisTick} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#4B5563" }} />
                <Tooltip {...commonTooltipProps} />
                <Area type="monotone" name="Field Costs" dataKey="amount" stroke={SUPERVISOR_COLOR} strokeWidth={3.5} fillOpacity={1} fill="url(#3dSup)" dot={<GlowingDot stroke={SUPERVISOR_COLOR} />} style={{ filter: `drop-shadow(0px 8px 16px ${SUPERVISOR_COLOR}60)` }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No Active Supervisor Logs" isDark={true} />}
        </ChartCard>

        {/* CHART E: WORKFORCE VOLUME OVERVIEW */}
        <ChartCard title="Workforce Strength" subtitle="Daily labor strength density summary" isDark={true}>
          {laborTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={laborTrendData} margin={{ top: 15, right: 15, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="3dLabor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={LABOR_COLOR} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={LABOR_COLOR} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: "#4B5563" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#4B5563" }} allowDecimals={false} />
                <Tooltip contentStyle={commonTooltipProps.contentStyle} />
                <Area type="monotone" name="Total Labours" dataKey="labours" stroke={LABOR_COLOR} strokeWidth={3.5} fillOpacity={1} fill="url(#3dLabor)" dot={<GlowingDot stroke={LABOR_COLOR} />} style={{ filter: `drop-shadow(0px 8px 16px ${LABOR_COLOR}60)` }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No Labor Strength Logged" isDark={true} />}
        </ChartCard>

      </div>

    </div>
  );
}

// 🔷 3D GLASSMORPHISM CONTAINER WRAPPER
function ChartCard({ title, subtitle, children, isDark = false }) {
  return (
    <div className={`p-6 rounded-[2.5rem] border transition-all duration-300 h-[370px] flex flex-col relative ${
      isDark 
        ? "bg-gradient-to-br from-slate-950 via-[#0B132B] to-slate-950 border-slate-900/80 shadow-[0_30px_60px_-15px_rgba(3,7,18,0.8)] before:absolute before:inset-0 before:rounded-[2.5rem] before:padding-[1px] before:bg-gradient-to-b before:from-white/10 before:to-transparent before:pointer-events-none" 
        : "bg-gradient-to-br from-white to-slate-50 border-slate-200/80 shadow-[0_20px_40px_-15px_rgba(148,163,184,0.12)]"
    } hover:-translate-y-1 hover:shadow-xl`}>
      <div className="mb-4 shrink-0 z-10">
        <h3 className={`font-black uppercase text-xs tracking-widest ${isDark ? "text-slate-100 drop-shadow-xs" : "text-slate-800"}`}>{title}</h3>
        <p className="text-[10px] text-slate-400 font-black uppercase mt-0.5 tracking-wide">{subtitle}</p>
      </div>
      <div className="flex-1 w-full relative min-h-0 z-10">
        {children}
      </div>
    </div>
  );
}

function EmptyState({ text, isDark = false }) {
  return (
    <div className={`absolute inset-0 flex items-center justify-center text-[10px] font-black tracking-wider uppercase ${
      isDark ? "text-slate-700" : "text-slate-400"
    }`}>
      {text}
    </div>
  );
}